import { buildAgentUrl } from '@/lib/agent/api';

export const LONG_PASTE_ATTACHMENT_MIN_CHARS = 2_000;

const IMAGE_EXTS: ReadonlySet<string> = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'avif', 'svg']);
const TEXT_EXTS: ReadonlySet<string> = new Set([
  'bash',
  'c',
  'cc',
  'cpp',
  'css',
  'go',
  'h',
  'hpp',
  'html',
  'java',
  'js',
  'json',
  'jsx',
  'kt',
  'log',
  'md',
  'py',
  'rs',
  'sh',
  'sql',
  'swift',
  'toml',
  'ts',
  'tsx',
  'txt',
  'xml',
  'yaml',
  'yml',
  'zsh',
]);
const TEXT_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/javascript',
  'application/json',
  'application/typescript',
  'application/x-python-code',
  'application/x-sh',
  'application/xml',
]);

export interface AttachmentDescriptor {
  name: string;
  path?: string;
  mime?: string;
}

export function attachmentBasename(path: string): string {
  const trimmed = path.replace(/\/+$/g, '') || path;
  const idx = trimmed.lastIndexOf('/');
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

export function attachmentDisplayName(pathOrName: string): string {
  const name = attachmentBasename(pathOrName);
  const match = name.match(/^[0-9a-f]{8}-(.+)$/);
  return match ? match[1] : name;
}

export function attachmentUploadUrl(path: string): string {
  return buildAgentUrl(`/project/upload/${encodeURIComponent(attachmentBasename(path))}`);
}

export function shouldAttachPastedText(text: string): boolean {
  return text.length > LONG_PASTE_ATTACHMENT_MIN_CHARS;
}

export function pastedSnippetFilename(now: Date = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('');
  return `pasted-snippet-${stamp}.txt`;
}

export function attachmentExtension(descriptor: AttachmentDescriptor): string | null {
  const name = descriptor.path ?? descriptor.name;
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return null;
  return name.slice(dot + 1).toLowerCase();
}

export function isImageAttachment(descriptor: AttachmentDescriptor): boolean {
  if (descriptor.mime?.startsWith('image/')) return true;
  const ext = attachmentExtension(descriptor);
  return ext != null && IMAGE_EXTS.has(ext);
}

export function isTextAttachment(descriptor: AttachmentDescriptor): boolean {
  if (descriptor.mime?.startsWith('text/')) return true;
  if (descriptor.mime && TEXT_MIME_TYPES.has(descriptor.mime)) return true;
  const ext = attachmentExtension(descriptor);
  return ext != null && TEXT_EXTS.has(ext);
}
